import type { RepeatableField } from "@/features/dynamic-variables/repeatable/types";
import { parseTokens } from "@/shared/utils/resolve-tokens";
import { useMemo } from "react";
import { type UseFormReturn, useWatch } from "react-hook-form";
import { getVariableControlKind } from "../control-registry";
import { normalizeVariableOptions } from "../control-utils";
import { ChoiceControl } from "../controls/choice-control";
import { ColorControl } from "../controls/color-control";
import { DateControl } from "../controls/date-control";
import { ScalarControl } from "../controls/scalar-control";
import { DataControl } from "../controls/structured-control";
import { AiCell } from "./ai-cell";
import { MediaCell } from "./media-cell";
import { useRepeatableDefinitions } from "./repeatable-definitions-context";
import type { RepeatableRow } from "./repeatable-row";

interface Props {
  field: RepeatableField;
  value: unknown;
  onChange: (value: unknown) => void;
  invalid?: boolean;
  /** The row this cell belongs to (AI prompts read sibling fields). */
  row?: RepeatableRow;
  /** Parent rows' fields, nearest last, for nested groups. */
  parentScope?: Record<string, unknown>;
  /** The group's key, so `{{group.field}}` prompts resolve. */
  groupKey?: string;
}

/**
 * AI cell on top of the version form: prompt inputs that are NOT row fields
 * (namespaced CRM fields, `sender_*`, anything the dependency promotion skips)
 * live at the top level of the form, and `form.getValues()` alone never
 * re-renders the cell when the sender fills one in. Subscribe to the prompt's
 * own dependency names so the cell leaves "Fill the variable …" on its own.
 */
const WatchedAiCell = ({
  form,
  prompt,
  rowScope,
  value,
  onChange,
  invalid,
}: {
  form: UseFormReturn<Record<string, unknown>>;
  prompt: string;
  rowScope: Record<string, unknown>;
  value: string;
  onChange: (value: unknown) => void;
  invalid: boolean;
}) => {
  const dependencies = useMemo(
    () =>
      Array.from(
        new Set(
          parseTokens(prompt).flatMap((segment) =>
            segment.type === "var" ? [segment.name] : [],
          ),
        ),
      ),
    [prompt],
  );
  const watched = useWatch({
    control: form.control,
    name: dependencies,
    disabled: dependencies.length === 0,
  });
  const scope = useMemo<Record<string, unknown>>(
    () => ({ ...form.getValues(), ...rowScope }),
    // `watched` is the subscription trigger: the values themselves are read
    // from the form so dotted paths resolve the same way as everywhere else.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form, rowScope, watched],
  );
  return (
    <AiCell
      prompt={prompt}
      scope={scope}
      value={value}
      onChange={onChange}
      invalid={invalid}
    />
  );
};

/**
 * One cell of a repeatable row, rendered with the same control the variable
 * would get as a top-level row: media pickers, calendar, colour, select /
 * multiselect (options from the org variable), the data sheet, and AI
 * generation. Falls back to a text input when the definition is unknown.
 */
export const FieldCell = ({
  field,
  value,
  onChange,
  invalid = false,
  row,
  parentScope,
  groupKey,
}: Props) => {
  const { definitions, form } = useRepeatableDefinitions();
  const definition = definitions.get(field.key);
  const kind = getVariableControlKind(field.type, field.key);
  const text = value == null ? "" : Array.isArray(value) ? "" : String(value);

  switch (kind) {
    case "color":
      return <ColorControl value={text} onChange={onChange} invalid={invalid} />;
    case "date":
      return <DateControl value={text} onChange={onChange} invalid={invalid} />;
    case "media":
      return (
        <MediaCell kind="image" value={text} onChange={onChange} invalid={invalid} />
      );
    case "video":
      return (
        <MediaCell kind="video" value={text} onChange={onChange} invalid={invalid} />
      );
    case "number":
      return (
        <ScalarControl type="number" value={text} onChange={onChange} invalid={invalid} />
      );
    case "iframe":
      return (
        <ScalarControl type="url" value={text} onChange={onChange} invalid={invalid} />
      );
    case "select":
    case "multiselect": {
      const options = normalizeVariableOptions(definition?.options);
      if (!options.length) break;
      const multiple = kind === "multiselect";
      return (
        <ChoiceControl
          multiple={multiple}
          options={options}
          value={multiple ? (Array.isArray(value) ? (value as string[]) : []) : text}
          onChange={onChange}
          invalid={invalid}
        />
      );
    }
    case "data":
      return <DataControl value={value} onChange={onChange} invalid={invalid} />;
    case "ai": {
      const prompt = definition?.prompt;
      if (!prompt) break;
      const rowScope: Record<string, unknown> = {
        ...(parentScope ?? {}),
        ...(groupKey && row ? { [groupKey]: row } : {}),
        ...(row ?? {}),
      };
      if (!form)
        return (
          <AiCell
            prompt={prompt}
            scope={rowScope}
            value={text}
            onChange={onChange}
            invalid={invalid}
          />
        );
      return (
        <WatchedAiCell
          form={form}
          prompt={prompt}
          rowScope={rowScope}
          value={text}
          onChange={onChange}
          invalid={invalid}
        />
      );
    }
    default:
      break;
  }
  // input / rich-text (v1) and any kind whose definition is missing.
  return <ScalarControl type="text" value={text} onChange={onChange} invalid={invalid} />;
};
